import { Fragment, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { TableIconButton } from '../../components/TableIconAction';
import {
  dataTableHeadClass,
  DataTablePanel,
  PageShell,
} from '../../components/DataTablePanel';
import { FilterField, ListFiltersBar } from '../../components/ListFiltersBar';
import {
  Callout,
  CollapseAllIcon,
  DateInput,
  ExpandAllIcon,
  ListEmptyState,
  LoadingSpinner,
  Select,
} from '../../components/ui';
import { ApiError, api } from '../../services/api';
import { t } from '../../i18n';
import { CompanyExpenseCategory } from '../../types/company-expense';
import { CompanyExpenseByVendorReport } from '../../types/reports';
import { formatDate, formatMoney } from '../../utils/format';
import { ReportExportButtons } from './ReportExportButtons';
import { exportCompanyExpenseByVendor } from './exportCompanyExpenseReports';

export function CompanyExpenseByVendorPage() {
  const labels = t().reports;
  const [categories, setCategories] = useState<CompanyExpenseCategory[]>([]);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const [report, setReport] = useState<CompanyExpenseByVendorReport | null>(
    null,
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void api.getCompanyExpenseCategories(false).then(setCategories);
  }, []);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const data = await api.getCompanyExpenseByVendorReport({
          dateFrom: dateFrom || undefined,
          dateTo: dateTo || undefined,
          categoryId: categoryId ? Number(categoryId) : undefined,
        });
        if (!cancelled) setReport(data);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof ApiError ? err.message : t().common.error);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    void load();
    return () => {
      cancelled = true;
    };
  }, [dateFrom, dateTo, categoryId]);

  const allExpanded =
    report != null &&
    report.data.length > 0 &&
    report.data.every((row) => expanded[row.vendorName]);

  return (
    <PageShell>
      <ListFiltersBar
        actions={
          <>
            {report && report.data.length > 0 ? (
              <TableIconButton
                label={allExpanded ? labels.hideInvoices : labels.showInvoices}
                onClick={() => {
                  if (allExpanded) {
                    setExpanded({});
                    return;
                  }
                  const next: Record<string, boolean> = {};
                  for (const row of report.data) next[row.vendorName] = true;
                  setExpanded(next);
                }}
              >
                {allExpanded ? (
                  <CollapseAllIcon className="h-4 w-4" />
                ) : (
                  <ExpandAllIcon className="h-4 w-4" />
                )}
              </TableIconButton>
            ) : null}
            {report ? (
              <ReportExportButtons
                disabled={!report}
                onExcel={() =>
                  exportCompanyExpenseByVendor({ report, format: 'excel' })
                }
                onPdf={() =>
                  exportCompanyExpenseByVendor({ report, format: 'pdf' })
                }
              />
            ) : null}
          </>
        }
      >
        <FilterField label={labels.dateFrom}>
          <DateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
        </FilterField>
        <FilterField label={labels.dateTo}>
          <DateInput value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
        </FilterField>
        <FilterField label={t().companyExpenses.category}>
          <Select
            value={categoryId}
            size="sm"
            showClearButton={Boolean(categoryId)}
            placeholder={labels.allCategories}
            options={categories.map((c) => ({ value: c.id, label: c.name }))}
            onChange={(e) => setCategoryId(e.target.value)}
          />
        </FilterField>
      </ListFiltersBar>

      {error ? (
        <Callout variant="danger" className="shrink-0">
          {error}
        </Callout>
      ) : null}

      <DataTablePanel>
        {loading ? (
          <LoadingSpinner message={t().common.loading} />
        ) : !report || report.data.length === 0 ? (
          <ListEmptyState title={t().common.empty} />
        ) : (
          <table className="min-w-full text-sm">
            <thead className={dataTableHeadClass}>
              <tr>
                <th className="px-3 py-2 text-left font-medium">
                  {labels.companyExpenseVendor}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.lineCount}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseNet}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseVat}
                </th>
                <th className="px-3 py-2 text-right font-medium">
                  {labels.expenseGross}
                </th>
              </tr>
            </thead>
            <tbody>
              {report.data.map((row) => (
                <Fragment key={row.vendorName}>
                  <tr
                    className="border-t border-line cursor-pointer hover:bg-surface-muted/40"
                    onClick={() =>
                      setExpanded((prev) => ({
                        ...prev,
                        [row.vendorName]: !prev[row.vendorName],
                      }))
                    }
                  >
                    <td className="px-3 py-2 font-medium">{row.vendorName}</td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {row.count}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(row.net)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(row.vat)}
                    </td>
                    <td className="px-3 py-2 text-right tabular-nums">
                      {formatMoney(row.gross)}
                    </td>
                  </tr>
                  {expanded[row.vendorName]
                    ? row.lines.map((line) => (
                        <tr
                          key={`${row.vendorName}-${line.expenseId}-${line.expenseDate}`}
                          className="border-t border-line bg-surface-muted/20 text-ink-muted"
                        >
                          <td className="px-3 py-2 pl-8">
                            <Link
                              to={`/company-expenses/${line.expenseId}/edit`}
                              className="text-brand hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {formatDate(line.expenseDate)}
                            </Link>
                            {line.documentNo
                              ? ` · ${line.documentNo}`
                              : ''}{' '}
                            · {line.categoryName}
                          </td>
                          <td className="px-3 py-2 text-right">—</td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatMoney(line.net)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatMoney(line.vat)}
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums">
                            {formatMoney(line.gross)}
                          </td>
                        </tr>
                      ))
                    : null}
                </Fragment>
              ))}
              <tr className="border-t border-line bg-surface-muted/40 font-semibold">
                <td className="px-3 py-2">{t().common.total}</td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {report.totals.count}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.net)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.vat)}
                </td>
                <td className="px-3 py-2 text-right tabular-nums">
                  {formatMoney(report.totals.gross)}
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </DataTablePanel>
    </PageShell>
  );
}
